import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import AgeGate from "@/components/AgeGate";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import BogoUpsell from "@/components/BogoUpsell";
import AIChat from "@/components/AIChat";
import BoomboxAirFreshener from "@/components/BoomboxAirFreshener";
import WelcomePopup from "@/components/WelcomePopup";
import ExitIntentModal from "@/components/ExitIntentModal";
import FreeShippingPopup from "@/components/FreeShippingPopup";
import { CartProvider } from "@/store/cart";
import { AuthProvider } from "@/store/auth";
import { AdsProvider } from "@/store/ads";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentStatus";
import AuthLogin from "@/pages/AuthLogin";
import AuthCallback from "@/pages/AuthCallback";
import AdminDashboard from "@/pages/AdminDashboard";
import Profile from "@/pages/Profile";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import GeoLanding from "@/pages/GeoLanding";
import About from "@/pages/About";
import FAQ from "@/pages/FAQ";
import HowItWorks from "@/pages/HowItWorks";
import GiftsHub from "@/pages/GiftsHub";
import GiftGuide from "@/pages/GiftGuide";
import CompareVsTree from "@/pages/CompareVsTree";
import ScentQuiz from "@/pages/ScentQuiz";
import GiftFinder from "@/pages/GiftFinder";
import LongevityCalculator from "@/pages/LongevityCalculator";
import ProgrammaticPage from "@/pages/ProgrammaticPage";
import BrainControl from "@/pages/BrainControl";
import SongLanding from "@/pages/SongLanding";
import GiftMatch from "@/pages/GiftMatch";
import UnwrapForDiscount from "@/pages/UnwrapForDiscount";
import SiteJsonLd from "@/components/SiteJsonLd";
import DieCutFilter from "@/components/DieCutFilter";

function StorefrontChrome() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const isAuth = location.pathname.startsWith("/login") || location.pathname.startsWith("/auth");
  if (isAdmin) {
    return <AIChat />; // Bree admin mode available inside admin panel
  }
  if (isAuth) return null;
  return (
    <>
      <CartDrawer />
      <BogoUpsell />
      <AIChat />
      <BoomboxAirFreshener />
      <AgeGate />
      <WelcomePopup />
      <ExitIntentModal />
      <FreeShippingPopup />
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  // If OAuth returned us here with a hash containing session_id, force the AuthCallback view
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Nav />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/gifts" element={<GiftsHub />} />
          <Route path="/gifts/:slug" element={<GiftGuide />} />
          <Route path="/compare/vs-tree-fresheners" element={<CompareVsTree />} />
          <Route path="/scent-quiz" element={<ScentQuiz />} />
          <Route path="/gift-finder" element={<GiftFinder />} />
          <Route path="/longevity-calculator" element={<LongevityCalculator />} />
          <Route path="/song" element={<SongLanding />} />
          <Route path="/gift-match" element={<GiftMatch />} />
          <Route path="/gift-match/:relationship/:occasion" element={<GiftMatch />} />
          <Route path="/unwrap" element={<UnwrapForDiscount />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/login" element={<AuthLogin />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/brain" element={<BrainControl />} />
          {/* 200 programmatic launch pages — Category A/B/C/D routes */}
          <Route path="/gifts/:occasion/:relationship" element={<ProgrammaticPage />} />
          <Route path="/gift-ideas/:modifier" element={<ProgrammaticPage />} />
          <Route path="/what-to-get/:slug" element={<ProgrammaticPage />} />
          <Route path="/shop/:hub" element={<ProgrammaticPage />} />
          {/* Category E — 10 info pages, explicit routes */}
          <Route path="/about-booty-breeze" element={<ProgrammaticPage />} />
          <Route path="/the-perfect-gag-gift" element={<ProgrammaticPage />} />
          <Route path="/novelty-air-freshener-gift-guide" element={<ProgrammaticPage />} />
          <Route path="/adult-novelty-gifts-faq" element={<ProgrammaticPage />} />
          <Route path="/gift-wrapping-and-shipping" element={<ProgrammaticPage />} />
          <Route path="/how-booty-breeze-are-made" element={<ProgrammaticPage />} />
          <Route path="/why-buy-from-a-small-business" element={<ProgrammaticPage />} />
          <Route path="/gift-message-ideas" element={<ProgrammaticPage />} />
          <Route path="/returns-and-guarantee" element={<ProgrammaticPage />} />
          <Route path="/bulk-and-wedding-orders" element={<ProgrammaticPage />} />
          {/* Programmatic geo landing pages — keep LAST so specific routes take priority */}
          <Route path="/:keyword/:slug" element={<GeoLanding />} />
        </Routes>
      </main>
      <Footer />
      <StorefrontChrome />
    </>
  );
}

function App() {
  return (
    <div className="App grain min-h-screen bg-[#050505] text-white">
      <HelmetProvider>
        <SiteJsonLd />
        <DieCutFilter />
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <AdsProvider>
                <AppRouter />
              </AdsProvider>
            </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
